import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageCategory = () => {
  const { CategoryID } = useParams();
  const navigate = useNavigate();
  const categoryIdNum = Number(CategoryID) || 0;

  // ---- State (matches API JSON) ----
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [widgetId, setWidgetId] = useState(null); // number|null
  const [isPublished, setIsPublished] = useState(false);
  const [nameAr, setNameAr] = useState("");
  const [bodyAr, setBodyAr] = useState("");

  // ---- Widgets for select ----
  const [widgets, setWidgets] = useState([]);
  // Build options from API data
  const widgetOptions = (Array.isArray(widgets) ? widgets : [])
    .filter((w) => parseInt(w.WidgetTypeId) === 5)
    .map((w) => ({
      value: w.Id,
      label: w.DisplayName ?? w.Name ?? String(w.Id),
    }));

  // Selected option from ID
  const selectedWidgetOption =
    widgetOptions.find((o) => o.value === widgetId) || null;

  // ---- Refs ----
  const fileInputRef = useRef(null);
  // put these above handleSubmit (anywhere in the component file)
  const isBlank = (v) => typeof v !== "string" || v.trim() === "";

  // ---- Loaders ----
  async function loadWidgets() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Widget/Get`);
      if (res.ok) setWidgets(await res.json());
      else toast.error("Failed to load widgets");
    } catch {
      toast.error("Failed to load widgets");
    }
  }

  async function loadCategory() {
    try {
      const res = await fetch(
        `${Variables.API_URL}Rasha/Category/Get/${categoryIdNum}`
      );
      if (!res.ok) return;
      const data = await res.json();
      console.log(JSON.stringify(data));

      // Accept camelCase or PascalCase
      setName(data.Category.name ?? data.Category.Name ?? "");
      setBody(data.Category.body ?? data.Category.Body ?? "");
      setImageUrl(data.Category.imageUrl ?? data.Category.ImageUrl ?? "");
      setWidgetId(data.Category.widgetId ?? data.Category.WidgetId ?? null);
      setIsPublished(
        typeof data.Category.isPublished === "boolean"
          ? data.Category.isPublished
          : Boolean(data.Category.IsPublished)
      );
      setNameAr(data.Category.nameAr ?? data.Category.NameAr ?? "");
      setBodyAr(data.Category.bodyAr ?? data.Category.BodyAr ?? "");
    } catch {
      toast.error("Failed to load Category");
    }
  }

  useEffect(() => {
    loadWidgets();
    if (categoryIdNum !== 0) loadCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdNum]);

  // ---- Normal <input type="file"> upload (<= 2MB) ----
  const onFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 2_000_000) {
      toast.error("Image is not accepted (max 2MB).");
      e.target.value = "";
      return;
    }

    const formdata = new FormData();
    formdata.append("Images", file);

    const requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    fetch(`${Variables.API_URL}UploadMedia/UploadImageToServer`, requestOptions)
      .then((response) => response.json())
      .then((result) => setImageUrl(result[0]))
      .catch((error) => console.error(error));
  };

  // ---- Validation ----
  function isValid() {
    if (isBlank(name)) {
      toast.error("Name is required");
      return false;
    }
    if (isBlank(body)) {
      toast.error("Body is required");
      return false;
    }

    if (isBlank(imageUrl)) {
      toast.error("Image is required (upload an image)");
      return false;
    }
    if (!widgetId || Number(widgetId) <= 0) {
      toast.error("Widget is required");
      return false;
    }
    if (isBlank(nameAr)) {
      toast.error("Name (Arabic) is required");
      return false;
    }
    if (isBlank(bodyAr)) {
      toast.error("Body (Arabic) is required");
      return false;
    }

    return true;
  }
  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      let payload;
      if (categoryIdNum === 0) {
        payload = {
          name,
          body,
          imageUrl,
          widgetId,
          isPublished,
          nameAr,
          bodyAr,
        };
      } else {
        payload = {
          id: categoryIdNum,
          name,
          body,
          imageUrl,
          widgetId,
          isPublished,
          nameAr,
          bodyAr,
        };
      }
      console.log(payload);

      const res = await fetch(`${Variables.API_URL}Rasha/Category/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(
        categoryIdNum === 0
          ? "Category added successfully"
          : "Category updated successfully"
      );
      navigate("/admin/rasha/Category");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>
              {categoryIdNum === 0 ? "Add" : `Manage ${name || "Category"}`}{" "}
              Category
            </h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Category">
                  Category <AiOutlineRight />
                </Link>
              </li>
              <li>{categoryIdNum === 0 ? "Add" : "Manage"} Category</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink
                    to="/admin/rasha/Category"
                    className="btn btn-secondary me-2"
                  >
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  {/* Title / TitleAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      id="name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Name (Arabic)</label>
                    <input
                      id="nameAr"
                      className="form-control"
                      value={nameAr}
                      onChange={(e) => setNameAr(e.target.value)}
                      placeholder="Name in Arabic"
                    />
                  </div>

                  {/* Body / BodyAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">Body</label>
                    <textarea
                      id="body"
                      className="form-control"
                      rows={5}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Category body"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Body (Arabic)</label>
                    <textarea
                      id="bodyAr"
                      className="form-control"
                      rows={5}
                      value={bodyAr}
                      onChange={(e) => setBodyAr(e.target.value)}
                      placeholder="Category body in Arabic"
                    />
                  </div>

                  {/* Publish toggle */}
                  <div className="form-group col-lg-3 published-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  {/* Widget select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget <span className="required">*</span>
                    </label>
                    <Select
                      options={widgetOptions}
                      value={selectedWidgetOption}
                      onChange={(opt) => setWidgetId(opt ? opt.value : null)}
                      placeholder="Select a widget…"
                      isClearable
                    />
                  </div>

                  {/* Image uploader (normal input) */}
                  <div className="col-lg-6">
                    <label className="mb-2">Image (max 2MB)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={onFileInputChange}
                    />
                    {imageUrl && (
                      <div className="mt-3">
                        <img
                          src={Variables.API_PUBLISHED + imageUrl}
                          alt="preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 200,
                            display: "block",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger mt-2"
                          onClick={() => setImageUrl("")}
                        >
                          Remove Image
                        </button>
                        <small className="text-muted d-block mt-2">
                          Uploaded URL: {imageUrl}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCategory;
